<script setup lang="ts">
import {useRoute} from "vue-router";
import AuthenticatedLayout from "@/layout/AuthenticatedLayout.vue";
import yapi from "@/lib/yapi";
import {Institution} from "@YenzaCT/sdk";
import {onMounted, ref, computed, defineAsyncComponent} from "vue";
import {useApiCall} from "@/composables/useApiCall";
import PageHeader from "@/components/PageHeader.vue";

import {useReportingStore} from "@/store/reporting";
import {useRoutedTabbedDashboard} from "@/composables/useRoutedTabbedDashboard";
import {useGlobalStore} from "@/store";

// Lazy load tab components
const DashboardTab = defineAsyncComponent(() => import("./tabs/DashboardTab.vue"));
const CohortsTab = defineAsyncComponent(() => import("./tabs/CohortsTab.vue"));
const StudentsTab = defineAsyncComponent(() => import("./tabs/StudentsTab.vue"));
const LicensesTab = defineAsyncComponent(() => import("./tabs/LicensesTab.vue"));
const ChoicesTab = defineAsyncComponent(() => import("../ui/ChoicesTab.vue"));
const AssessmentsTab = defineAsyncComponent(() => import("../ui/AssessmentsTab.vue"));

const route = useRoute();
const store = useGlobalStore();
const {executeApi} = useApiCall();
const id = route.params.institutionId as string;
const tenantId = route.params.tenantId as string;
const institution = ref<Institution>();
const reportingStore = useReportingStore();
const {activeTab} = useRoutedTabbedDashboard("institutionDashboard", id);
const initialized = ref(false);

const canAccessSpecificTenant = store.canAccessSpecificTenant(tenantId);

// Map tab names to components
const tabComponents = {
  dashboard: DashboardTab,
  cohorts: CohortsTab,
  students: StudentsTab,
  licenses: LicensesTab,
  choices: ChoicesTab,
  assessments: AssessmentsTab,
};

// Get the current tab component
const currentTabComponent = computed(() =>
  tabComponents[activeTab.value as keyof typeof tabComponents] || DashboardTab);

onMounted(async () => {
  initialized.value = false;
  // Set filtering in the store to ensure all metrics and reports are scoped to this institution
  reportingStore.setFiltersAndRefresh({institutions: [id]});
  await fetchInstitution();
  initialized.value = true;
});

async function fetchInstitution() {
  const result = await executeApi(async () => {
    const response = await yapi.admin.institution.get(id);
    return response.data;
  });
  if (result) {
    institution.value = result;
  }
}
</script>

<template>
  <authenticated-layout :back-route="{ name: 'schoolsDashboard' }"
                        :breadcrumbs="[
                          { text: 'My Schools', to: { name: 'schoolsDashboard' } },
                          { text: institution?.tenantMeta?.title || '',
                            to: canAccessSpecificTenant ? { name: 'schoolsDashboardWithTenant',
                                                            params: { tenantId: institution?.tenant } } : {
                                                              name: 'schoolsDashboardWithoutTenant'
                                                            }
                          },
                          { text: institution?.title || '' }
                        ]">

    <page-header
      :title="institution?.title"
    />

    <v-container class="mt-0 mx-auto pa-0 border-b" fluid>
      <v-tabs v-model="activeTab" grow>
        <v-tab value="dashboard">
          Dashboard
        </v-tab>
        <v-tab value="cohorts">
          Cohorts
        </v-tab>
        <v-tab value="students">
          Students
        </v-tab>
        <v-tab value="licenses">
          Licenses
        </v-tab>
        <v-tab value="choices">
          Choices
        </v-tab>
        <v-tab value="assessments">
          Assessments
        </v-tab>
      </v-tabs>
    </v-container>

    <div v-if="institution">
      <v-container class="mx-auto" fluid>
        <component
          v-if="initialized"
          :is="currentTabComponent"
          :institution="institution"
        />
      </v-container>
    </div>

  </authenticated-layout>
</template>

<style scoped>

</style>
